import { formatCount } from "utils/chartsPalette";
import { WIDGET } from "../../layout/catalog";

// Labels and derived figures for the startup-platform widgets, read from
// startup-backend's /admin/platform-analytics payload. Same rule as
// content.js: every variant reads these, so a figure is fixed in one place.

const values = (daily, field = "count") => (daily ?? []).map((d) => d?.[field] ?? 0);
const plural = (n, one, many = `${one}s`) => `${formatCount(n)} ${n === 1 ? one : many}`;

// "04m 32s" / "1h 05m", matching the payload's own *_formatted strings.
export const formatDuration = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined) return "—";
  const s = Math.max(0, Math.round(totalSeconds));
  if (s >= 3600) return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}m`;
  return `${String(Math.floor(s / 60)).padStart(2, "0")}m ${String(s % 60).padStart(2, "0")}s`;
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : null;

export const profileCompletion = (platform) => {
  const pc = platform?.profile_completion;
  return {
    buckets: pc?.buckets ?? [],
    total: pc?.total_startups ?? 0,
    subtitle: pc
      ? pc.total_startups
        ? `${plural(pc.total_startups, "startup")} created in this range · average ${pc.average_completion ?? "—"}% complete`
        : "No startups created in this range"
      : "",
    footnote: pc
      ? `${formatCount(pc.above_invite_gate ?? 0)} at ${pc.investor_invite_gate ?? 50}%+ can invite investors · scored with the current weights`
      : "",
  };
};

export const uploads = (platform) => {
  const u = platform?.uploads;
  const analyzer = u?.pitch_deck_analyzer?.uploaded_in_range?.value;
  // Card keys and labels come from the layout catalog, so the Customize
  // drawer and the dashboard can never disagree about what a card is called.
  return WIDGET.pitch_uploads.items.map((c) => {
    const b = u?.[c.key];
    const inRange = b?.uploaded_in_range;
    return {
      key: c.key,
      label: c.label,
      total: b?.total,
      coverage: b?.coverage_pct,
      inRange: inRange?.value,
      delta: inRange?.delta_pct,
      series: values(inRange?.daily),
      footnote: [
        b?.coverage_pct != null ? `${b.coverage_pct}% of startups have one` : null,
        b?.undated ? `${formatCount(b.undated)} uploaded before tracking` : null,
        c.key === "pitch_deck" && analyzer ? `+${formatCount(analyzer)} via Pitch Deck Analyzer` : null,
      ].filter(Boolean).join(" · "),
    };
  });
};

export const activeUsers = (platform) => {
  const a = platform?.active_users;
  const since = fmtDate(a?.tracking_since);
  return {
    value: a?.active_users?.value,
    delta: a?.active_users?.delta_pct,
    series: values(a?.daily),
    tracked: Boolean(a?.tracking_since),
    subtitle: since ? `Founders who opened the app · tracked since ${since}` : "Founders who opened the app",
    stats: [
      { label: "Avg daily active", value: a?.avg_daily_active ?? "—" },
      { label: "Active startups", value: formatCount(a?.active_startups?.value) },
      { label: "Of all founders", value: a?.active_rate_pct != null ? `${a.active_rate_pct}%` : "—" },
    ],
    lastLogin: a?.last_login_in_range?.startup,
  };
};

export const sessionTime = (platform) => {
  const s = platform?.session_time;
  return {
    seconds: s?.avg_session_seconds?.value,
    formatted: s?.avg_session_formatted ?? formatDuration(s?.avg_session_seconds?.value),
    delta: s?.avg_session_seconds?.delta_pct,
    series: values(s?.daily, "avg_seconds"),
    distribution: s?.distribution ?? [],
    measured: s?.measured_sessions ?? 0,
    stats: [
      { label: "Sessions", value: formatCount(s?.sessions?.value) },
      { label: "Per founder", value: s?.sessions_per_user ?? "—" },
      { label: "Time in app", value: formatDuration(s?.total_seconds) },
    ],
  };
};

export const activity = (platform) => {
  const byKey = Object.fromEntries((platform?.activity?.metrics ?? []).map((m) => [m.key, m]));
  // Rounds, data rooms and assessments have widgets of their own, so the
  // catalog leaves them out of this one.
  return WIDGET.platform_activity.items.map((c) => ({
    key: c.key,
    label: c.label,
    value: byKey[c.key]?.value,
    delta: byKey[c.key]?.delta_pct,
    previous: byKey[c.key]?.previous,
    series: values(byKey[c.key]?.daily),
  }));
};

export const rounds = (platform) => {
  const r = platform?.rounds;
  return {
    value: r?.created?.value,
    delta: r?.created?.delta_pct,
    series: values(r?.created?.daily),
    stats: [
      { label: "All-time rounds", value: formatCount(r?.total_rounds) },
      { label: "Startups with a round", value: formatCount(r?.startups_with_rounds) },
      { label: "Rounds per startup", value: r?.avg_rounds_per_startup ?? "—" },
      { label: "Marked as raised", value: formatCount(r?.raised_rounds) },
    ],
  };
};

export const startupViews = (platform) => {
  const v = platform?.startup_views;
  return {
    value: v?.profile_views?.value,
    delta: v?.profile_views?.delta_pct,
    series: values(v?.daily, "views"),
    stats: [
      { label: "Unique investors", value: formatCount(v?.unique_investors?.value), delta: v?.unique_investors?.delta_pct },
      { label: "Startups viewed", value: formatCount(v?.startups_viewed?.value), delta: v?.startups_viewed?.delta_pct },
      { label: "Avg time on profile", value: v?.avg_time_on_profile_formatted ?? "—", delta: v?.avg_time_on_profile_seconds?.delta_pct },
      { label: "Page visits", value: formatCount(v?.page_visits?.value), delta: v?.page_visits?.delta_pct },
    ],
    interactions: (v?.interactions ?? []).map((i) => ({ key: i.key, label: i.label, count: i.value ?? 0, delta: i.delta_pct })),
    top: (v?.top_startups ?? []).map((t) => ({ label: t.name, count: t.views, investors: t.unique_investors })),
  };
};

export const dataRooms = (platform) => {
  const d = platform?.data_rooms;
  const t = d?.totals ?? {};
  return {
    value: d?.created?.value,
    delta: d?.created?.delta_pct,
    series: values(d?.created?.daily),
    totals: t,
    access: [
      { key: "link", label: "Anyone with the link", count: t.link_access ?? 0 },
      { key: "restricted", label: "Named investors only", count: t.restricted_access ?? 0 },
    ],
    stats: [
      { label: "All-time rooms", value: formatCount(t.data_rooms) },
      { label: "Startups with a room", value: formatCount(t.startups_with_data_room) },
      { label: "Docs per room", value: t.avg_documents_per_room ?? "—" },
      { label: "PIN protected", value: formatCount(t.pin_protected) },
    ],
    engagement: [
      { label: "Room views", value: formatCount(d?.views?.value), delta: d?.views?.delta_pct },
      { label: "Investors viewing", value: formatCount(d?.unique_investors?.value), delta: d?.unique_investors?.delta_pct },
      { label: "Document opens", value: formatCount(d?.document_opens?.value), delta: d?.document_opens?.delta_pct },
      { label: "Docs uploaded", value: formatCount(d?.documents_uploaded?.value), delta: d?.documents_uploaded?.delta_pct },
    ],
  };
};
